
import React, { Fragment, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dropdown, ButtonGroup } from 'react-bootstrap';
import { IndexLinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { DropdownTopWindowButton } from '../../../components/DropdownButton';
import moment from 'moment';

import {
  IoIosSchool,
  IoIosNotifications,
  IoIosNotificationsOff,
  IoIosConstruct,
  IoIosListBox,
  IoIosCog,
} from "react-icons/io";

// Other
import Private from '../../../components/Private';
import RelearnModalForm from './RelearnModalForm';
import ArchiveDeviceModal from './ArchiveDeviceModal';
import ReportEventModal from './ReportEventModal';
import AddLinkModal from './AddLinkModal';

// actions
import { fetchDeviceInfo, updateDeviceMuteStatus } from '../actions';
import { getDevice, getDeviceHasProductCode } from '../selectors';
import { getCurrentOrganisationHasProductCode } from '../../organisation/selectors';
import { isFitMachine, isFitPower } from '../utils';

function DeviceContextMenu({
  as = ButtonGroup,
  title = '',
  device,
  fetchDeviceInfo,
  updateDeviceMuteStatus,
  // remove device id from props to spread
  deviceId,
  userCanCreateAlarm,
  hasDeviceSettings,
  hasFitMachineSettings,
  hasFitPowerSettings,
  ...props
}) {

  const { t } = useTranslation();
  const muteFor48h = useCallback(e => {
    e.preventDefault();
    updateDeviceMuteStatus(device, {
      time: moment.duration({ hours: 48 }).asSeconds(),
      timeString: '48 hours',
    });
  }, [device]);

  const muteFor1w = useCallback(e => {
    e.preventDefault();
    updateDeviceMuteStatus(device, {
      time: moment.duration({ week: 1 }).asSeconds(),
      timeString: '1 week',
    });
  }, [device]);

  const unmute = useCallback(e => {
    e.preventDefault();
    updateDeviceMuteStatus(device, { time: 0 });
  }, [device]);

  // skip if device has not yet been loaded
  if (!device) {
    return null;
  }

  return (
    <DropdownTopWindowButton
      as={as}
      title={title}
      // add defaults
      menuAlign="right"
      variant="outline-secondary"
      // allow overrides
      {...props}
    >
      { device.mute_advisory_for === 0 && isFitMachine(device) ? (
        <Fragment>
          <Dropdown.Header>
            {t('components.equipment.device-context-menu.not-muted')}
          </Dropdown.Header>
          <Dropdown.Item onClick={muteFor48h}>
            <IoIosNotificationsOff size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.mute-48-hours')}
            </span>
          </Dropdown.Item>
          <Dropdown.Item onClick={muteFor1w}>
            <IoIosNotificationsOff size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.mute-1-week')}
            </span>
          </Dropdown.Item>
          <Dropdown.Divider />
        </Fragment>
      ) : device.mute_advisory_for > 0 && isFitMachine(device) ? (
        <Fragment>
          {/*Can't use moment.local() on a duration.*/}
          {/*https://stackoverflow.com/questions/29004216/how-can-i-use-duration-on-local-moment-instance*/}
          <Dropdown.Header>{t('components.equipment.device-context-menu.muted-for', {
            muteTime: moment.duration({
              seconds: device.mute_advisory_for
            }).locale(i18n.resolvedLanguage).humanize()
          })}</Dropdown.Header>
          <Dropdown.Item onClick={unmute}>
            <IoIosNotifications size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.unmute')}
            </span>
          </Dropdown.Item>
          <Dropdown.Divider />
        </Fragment>
      ) : null}
      {userCanCreateAlarm &&
        <Fragment>
          <Dropdown.Header>
            {t('components.equipment.device-context-menu.alarms-and-events')}
          </Dropdown.Header>
          <ReportEventModal device={device} />
          <Dropdown.Divider />
        </Fragment>
      }
      <Private minUserType="Admin">
        <Dropdown.Header>
          {t('components.equipment.device-context-menu.event-impact')}
        </Dropdown.Header>
        <IndexLinkContainer to={`/equipment/${device.id}/impact`}>
          <Dropdown.Item>
            <IoIosConstruct size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.configure-rates')}
            </span>
          </Dropdown.Item>
        </IndexLinkContainer>
        <IndexLinkContainer to={`/equipment/${device.id}/impact_summary`}>
          <Dropdown.Item>
            <IoIosListBox size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.event-impact-summary')}
            </span>
          </Dropdown.Item>
        </IndexLinkContainer>
        <Dropdown.Divider />
      </Private>
      <Private minUserType="Admin">
        <Dropdown.Header>{t('components.equipment.device-context-menu.admin-tools')}</Dropdown.Header>
        {hasFitPowerSettings && isFitPower(device) && <IndexLinkContainer to={`/equipment/${device.id}/settings`}>
          <Dropdown.Item>
            <IoIosCog size="1.2em" /> <span>Settings</span>
          </Dropdown.Item>
        </IndexLinkContainer>}
        {hasFitMachineSettings && isFitMachine(device) && <IndexLinkContainer to={`/equipment/${device.id}/settings`}>
          <Dropdown.Item>
            <IoIosCog size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.advanced-configuration-utility')}
            </span>
          </Dropdown.Item>
        </IndexLinkContainer>}
        <AddLinkModal
          device={device}
          header={t('components.equipment.device-context-menu.link-device')}
        />
        {isFitMachine(device) && <RelearnModalForm deviceId={device.id}>
          <Dropdown.Item eventKey="2">
            <IoIosSchool size="1.2em" /> <span>
              {t('components.equipment.device-context-menu.restart-learning')}
            </span>
          </Dropdown.Item>
        </RelearnModalForm>}
        <ArchiveDeviceModal
          deviceId={device.id}
          onSuccess={fetchDeviceInfo}
        />
      </Private>
    </DropdownTopWindowButton>
  );
}

const mapStateToProps = (state, { deviceId } ) => ({
  device: getDevice(state, deviceId),
  userCanCreateAlarm: getDeviceHasProductCode(state, deviceId, 'user_alarms'),
  hasDeviceSettings: getCurrentOrganisationHasProductCode(state, 'device_settings'),
  hasFitMachineSettings: getCurrentOrganisationHasProductCode(state, 'fmsettings'),
  hasFitPowerSettings: getCurrentOrganisationHasProductCode(state, 'fpsettings'),
});

export default connect(mapStateToProps, {
  fetchDeviceInfo,
  updateDeviceMuteStatus,
})(DeviceContextMenu);
